import React, { useEffect, useState, useMemo } from 'react';
import { SITE_TITLE } from '../../utils/constants';
import { seo } from '../../utils/seo';
import { Validation } from '../../utils/validation';
import Loader from '../../components/Loader/Loader';
import logo from '../../images/techtop-full-logo.svg';
import AlertMessage from '../../components/AlertMessage/AlertMessage';
import CustomerOnboardingService from '../../services/CustomerOnboardingService/CustomerOnboardingService';
import Step1 from './steps/step-1';
import Step2 from './steps/step-2';
import Step3 from './steps/step-3';
import './customer-onboarding.css';

const CustomerOnBoarding = (props) => {
    const today = new Date().toISOString().split('T')[0];

    // Extract token from URL route params or query string
    const tokenFromParams = props?.match?.params?.token || '';
    const tokenFromQuery = typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('token') || ''
        : '';
    const token = tokenFromParams || tokenFromQuery;

    const [activeStep, setActiveStep] = useState(1);
    const [isLoading, setIsLoading] = useState(Boolean(token));
    const [isSubmit, setIsSubmit] = useState(false);
    const [pageStatus, setPageStatus] = useState(token ? 'LOADING' : 'NO_TOKEN');
    const [invalidExpiredMsg, setInvalidExpiredMsg] = useState('');
    const [alert, setAlert] = useState({ type: '', message: '' });
    const [errors, setErrors] = useState({});
    const [prefilledFields, setPrefilledFields] = useState({});

    const [formData, setFormData] = useState({
        form_token: token || '',
        application_date: today,
        company_name: '',
        techtop_rep_name: '',
        physical_address: '',
        mailing_address: '',
        city: '',
        county: '',
        state: '',
        country: 'USA',
        zip_code: '',
        tax_id: '',
        is_business_tax_exempt: 'No',
        telephone_number: '',
        fax_number: '',
        accounts_payable_contact_name: '',
        email_address: '',
        // Step 3 fields
        trade_references: [
            { business_name: '', email: '', fax: '', contact_name: '' },
            { business_name: '', email: '', fax: '', contact_name: '' },
            { business_name: '', email: '', fax: '', contact_name: '' }
        ],
        bank_references: [
            { bank_name: '', account_number: '', telephone: '', contact_name: '' },
            { bank_name: '', account_number: '', telephone: '', contact_name: '' }
        ],
        amount_credit_requested: '25000',
        other_amount_val: '',
        signature: '',
        submission_date: today,
        print_name: '',
        title: ''
    });

    const [files, setFiles] = useState({
        tax_exempt_file: null,
        upload_reseller_cert: null,
        financial_statements_file: null
    });

    const onboardingService = useMemo(() => new CustomerOnboardingService(), []);

    // Set SEO Meta
    useEffect(() => {
        seo({
            title: SITE_TITLE + ' || New Customer Form',
            metaDescription: 'New Customer Form - Techtop Electric Motors',
        });
    }, []);

    // Fetch prefill data if token exists
    useEffect(() => {
        if (!token) {
            setPageStatus('NO_TOKEN');
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        setPageStatus('LOADING');
        setAlert({ type: '', message: '' });

        onboardingService.getCustomerAccountSetupFormData(token).then((response) => {
            setIsLoading(false);
            const resData = response?.data;
            if (response?.status === 200 && resData?.status) {
                const data = resData.data || {};
                // If token or setup is already used / completed
                if (data.is_used || data.status === 'completed') {
                    setPageStatus('ALREADY_USED');
                    return;
                }

                setPageStatus('READY');

                // Determine which fields were auto-populated from database
                const dbPopulated = {};
                const checkAndSet = (fieldName, val) => {
                    if (val !== null && val !== undefined && String(val).trim() !== '') {
                        dbPopulated[fieldName] = true;
                    }
                };

                checkAndSet('company_name', data.company_name);
                checkAndSet('application_date', data.application_date);
                checkAndSet('techtop_rep_name', data.techtop_rep_name || data.sales_rep);
                checkAndSet('physical_address', data.physical_address || data.address);
                checkAndSet('mailing_address', data.mailing_address);
                checkAndSet('city', data.city);
                checkAndSet('county', data.county);
                checkAndSet('state', data.state);
                checkAndSet('zip_code', data.zip_code || data.postal_code);
                checkAndSet('tax_id', data.tax_id);
                if (data.is_business_tax_exempt !== null && data.is_business_tax_exempt !== undefined && data.is_business_tax_exempt !== '') {
                    dbPopulated.is_business_tax_exempt = true;
                }
                checkAndSet('telephone_number', data.telephone_number);
                checkAndSet('fax_number', data.fax_number);
                checkAndSet('accounts_payable_contact_name', data.accounts_payable_contact_name);
                checkAndSet('email_address', data.email_address || data.email);

                setPrefilledFields(dbPopulated);

                setFormData((prev) => ({
                    ...prev,
                    prefilled_fields: dbPopulated,
                    form_token: token,
                    company_name: data.company_name || prev.company_name,
                    application_date: data.application_date || prev.application_date,
                    techtop_rep_name: data.techtop_rep_name || prev.techtop_rep_name,
                    physical_address: data.physical_address || prev.physical_address,
                    mailing_address: data.mailing_address || prev.mailing_address,
                    city: data.city || prev.city,
                    county: data.county || prev.county,
                    state: data.state || prev.state,
                    country: data.country || prev.country,
                    zip_code: data.zip_code || prev.zip_code,
                    tax_id: data.tax_id || prev.tax_id,
                    is_business_tax_exempt: data.is_business_tax_exempt ? 'Yes' : 'No',
                    telephone_number: data.telephone_number || prev.telephone_number,
                    fax_number: data.fax_number || prev.fax_number,
                    accounts_payable_contact_name: data.accounts_payable_contact_name || prev.accounts_payable_contact_name,
                    email_address: data.email_address || data.email || prev.email_address,
                    amount_credit_requested: data.amount_credit_requested ? String(parseInt(data.amount_credit_requested, 10)) : prev.amount_credit_requested,
                    signature: data.signature || prev.signature,
                    title: data.title || prev.title,
                    print_name: data.print_name || prev.print_name,
                    submission_date: data.submission_date || prev.submission_date,
                    trade_references: (data.reference_trade && data.reference_trade.length > 0)
                        ? data.reference_trade
                        : prev.trade_references,
                    bank_references: (data.reference_bank && data.reference_bank.length > 0)
                        ? data.reference_bank
                        : prev.bank_references
                }));
            } else if (response?.status === 410 || resData?.is_expired) {
                setPageStatus('INVALID_OR_EXPIRED');
                setInvalidExpiredMsg(
                    resData?.message || 'This account setup link has expired. Please contact your Techtop representative for a new link.'
                );
            } else {
                setPageStatus('INVALID_OR_EXPIRED');
                setInvalidExpiredMsg(resData?.message || 'Invalid or expired onboarding link.');
            }
        }).catch(() => {
            setIsLoading(false);
            setPageStatus('INVALID_OR_EXPIRED');
            setInvalidExpiredMsg('Unable to verify setup token. The link may be invalid or expired.');
        });
    }, [token, onboardingService]);

    // Handle generic text/select input change
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    // Handle File inputs
    const handleFileChange = (field, file) => {
        setFiles((prev) => ({ ...prev, [field]: file }));
    };

    // Handle File removal
    const handleRemoveFile = (field) => {
        setFiles((prev) => ({ ...prev, [field]: null }));
        const inputElem = document.getElementById(field);
        if (inputElem) inputElem.value = '';
    };

    // Handle Trade References array
    const handleTradeRefChange = (index, field, value) => {
        setFormData((prev) => {
            const list = [...(prev.trade_references || [])];
            if (!list[index]) list[index] = {};
            list[index] = { ...list[index], [field]: value };
            return { ...prev, trade_references: list };
        });
    };

    // Handle Bank References array
    const handleBankRefChange = (index, field, value) => {
        setFormData((prev) => {
            const list = [...(prev.bank_references || [])];
            if (!list[index]) list[index] = {};
            list[index] = { ...list[index], [field]: value };
            return { ...prev, bank_references: list };
        });
    };

    // Handle Amount of Credit Requested selection
    const handleCreditAmountChange = (amount) => {
        setFormData((prev) => ({
            ...prev,
            amount_credit_requested: amount
        }));
    };

    // Validate Step 1 (company_name only per requirement)
    const validateStep1 = () => {
        const validationErrors = Validation(
            { company_name: formData.company_name?.trim() },
            ['company_name']
        );
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return false;
        }
        setErrors({});
        return true;
    };

    // Stepper navigation handler
    const goToStep = (stepNum) => {
        if (stepNum > 1 && !validateStep1()) {
            setActiveStep(1);
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }
        setActiveStep(stepNum);
        scrollToTop();
    };

    const scrollToTop = () => {
        try {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } catch (e) {
            window.scrollTo(0, 0);
        }
        if (document.documentElement) document.documentElement.scrollTop = 0;
        if (document.body) document.body.scrollTop = 0;
    };

    // Handle Final Submission (from Step 3)
    const handleSubmit = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setAlert({ type: '', message: '' });
        scrollToTop();

        // Apply validation currently for company name only
        if (!validateStep1()) {
            setActiveStep(1);
            setAlert({
                type: 'error',
                message: 'Please provide the required company name to proceed with submission.'
            });
            scrollToTop();
            return;
        }

        setIsSubmit(true);

        try {
            const payload = new FormData();
            payload.append('form_token', formData.form_token || token || '');
            payload.append('company_name', formData.company_name?.trim() || '');
            payload.append('application_date', formData.application_date || '');
            payload.append('techtop_rep_name', formData.techtop_rep_name?.trim() || '');
            payload.append('physical_address', formData.physical_address?.trim() || '');
            payload.append('mailing_address', formData.mailing_address?.trim() || '');
            payload.append('city', formData.city?.trim() || '');
            payload.append('county', formData.county?.trim() || '');
            payload.append('state', formData.state?.trim() || '');
            payload.append('country', formData.country?.trim() || 'USA');
            payload.append('zip_code', formData.zip_code?.trim() || '');
            payload.append('tax_id', formData.tax_id?.trim() || '');
            payload.append('is_business_tax_exempt', formData.is_business_tax_exempt === 'Yes');
            payload.append('telephone_number', formData.telephone_number?.trim() || '');
            payload.append('fax_number', formData.fax_number?.trim() || '');
            payload.append('accounts_payable_contact_name', formData.accounts_payable_contact_name?.trim() || '');
            payload.append('email_address', formData.email_address?.trim() || '');

            // Amount requested
            const creditAmt = formData.amount_credit_requested === 'other'
                ? formData.other_amount_val
                : formData.amount_credit_requested;
            payload.append('amount_credit_requested', creditAmt || '0');

            payload.append('signature', formData.signature?.trim() || '');
            payload.append('submission_date', formData.submission_date || today);
            payload.append('print_name', formData.print_name?.trim() || '');
            payload.append('title', formData.title?.trim() || '');

            // Filter out blank references
            const cleanTrade = (formData.trade_references || []).filter(
                (r) => r.business_name || r.email || r.fax || r.contact_name
            );
            const cleanBank = (formData.bank_references || []).filter(
                (r) => r.bank_name || r.account_number || r.telephone || r.contact_name
            );
            payload.append('trade_references', JSON.stringify(cleanTrade));
            payload.append('bank_references', JSON.stringify(cleanBank));

            // Attach Files
            if (files.upload_reseller_cert) {
                payload.append('upload_reseller_cert', files.upload_reseller_cert);
            }
            if (files.tax_exempt_file) {
                payload.append('tax_exempt_file', files.tax_exempt_file);
            }
            if (files.financial_statements_file) {
                payload.append('financial_statements_file', files.financial_statements_file);
            }

            const response = await onboardingService.saveCustomerAccountSetupForm(payload);
            setIsSubmit(false);
            const resData = response?.data;

            if (resData && (resData.status === 'success' || resData.response === 'success' || response.status === 200)) {
                setPageStatus('SUBMITTED');
                setAlert({
                    type: 'success',
                    message: 'Form Submitted Successfully! Your customer setup application has been routed to billing@techtopind.com for account creation.'
                });
                scrollToTop();
            } else {
                const errorMsg = resData?.message || resData?.error || 'Failed to submit the form. Please verify and try again.';
                setAlert({ type: 'error', message: errorMsg });
                scrollToTop();
            }
        } catch (error) {
            setIsSubmit(false);
            setAlert({ type: 'error', message: 'An unexpected error occurred while submitting. Please try again.' });
            scrollToTop();
        }
    };

    // Render View: User tries to access URL without token
    const renderNoToken = () => (
        <div id="no_token_container" className="customer-status-card text-center">
            <div className="status-card-icon-wrapper warning-theme">
                <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
            </div>
            <h2 className="status-card-title">Onboarding Token Required</h2>
            <p className="status-card-subtitle">
                A valid onboarding token is required in the URL to access the New Customer Form.
            </p>
        </div>
    );

    // Render View: Invalid or expired link
    const renderInvalidExpired = () => (
        <div id="invalid_expired_container" className="customer-status-card text-center">
            <div className="status-card-icon-wrapper danger-theme">
                <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="8" x2="12" y2="12"></line>
                    <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
            </div>
            <h2 className="status-card-title">Invalid or Expired Link</h2>
            <p className="status-card-subtitle">
                {invalidExpiredMsg || 'This customer onboarding setup link is either invalid or has expired.'}
            </p>
        </div>
    );

    // Render View: Application just submitted successfully
    const renderSubmittedSuccess = () => (
        <div id="application_submitted_container" className="customer-status-card text-center">
            <div className="status-card-icon-wrapper success-theme">
                <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            </div>
            <h2 className="status-card-title">Application Submitted Successfully</h2>
            <p className="status-card-subtitle">
                Thank you! Your New Customer application has been submitted and sent to our team for processing.
            </p>
        </div>
    );

    // Render View: Token already used (shown when user opens the URL again next time)
    const renderAlreadyUsed = () => (
        <div id="already_used_container" className="customer-status-card text-center">
            <div className="status-card-icon-wrapper success-theme">
                <svg width="38" height="38" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
            </div>
            <h2 className="status-card-title">Link Already Used</h2>
            <p className="status-card-subtitle">
                This customer onboarding setup link has already been used to submit an application.
            </p>

            <div className="status-card-body-box">
                <p className="mb-2"><strong>Submission Status:</strong></p>
                <p className="mb-2">
                    Your customer account application has been received and routed to the Techtop Billing and Customer Service department.
                </p>
                {/* <p className="mb-0 text-muted">
                    For record integrity and account protection, each onboarding link can only be submitted once. If you need to make changes, update references, or check the status of your account, please contact our team at <strong>billing@techtopind.com</strong> or call <strong>(678) 436-5540</strong>.
                </p> */}
            </div>

            {/* <div className="status-card-actions">
                <a href="mailto:billing@techtopind.com?subject=Customer%20Account%20Setup%20Inquiry" className="status-primary-btn">
                    Contact Billing Department
                </a>
                <a href="/" className="status-secondary-btn">
                    Visit Techtop Home
                </a>
            </div> */}
        </div>
    );

    // Render View: Active Onboarding Form
    const renderNewCustomerForm = () => (
        <div className="row">
            <div className="col-12">
                <div className="customer-doc-card">
                    <form id="newCustomerForm" onSubmit={handleSubmit} noValidate>
                        {/* Top Document Header Block */}
                        <div className="doc-header-row">
                            <div className="doc-brand-block">
                                <img
                                    src={logo}
                                    alt="TECHTOP"
                                    className="doc-brand-logo"
                                />
                            </div>
                            <div className="doc-title-block">
                                <h2 className="doc-main-title">New Customer Form</h2>
                                <p className="doc-sub-title">Techtop Industries Incorporated, A Georgia Corporation</p>
                            </div>
                            <div className="doc-email-box">
                                <div className="doc-email-box-label">Complete and E-Mail to:</div>
                                <a href="mailto:billing@techtopind.com" className="doc-email-box-address">
                                    billing@techtopind.com
                                </a>
                            </div>
                        </div>

                        {/* Stepper Header Navigation */}
                        <div className="form-stepper-wrapper mb-4">
                            <div
                                className={`stepper-item ${activeStep === 1 ? 'active' : ''} ${activeStep > 1 ? 'completed' : ''}`}
                                id="stepper_indicator_1"
                                onClick={() => goToStep(1)}
                            >
                                <div className="step-circle" id="circle_1">
                                    {activeStep > 1 ? '✔' : '1'}
                                </div>
                                <div className="step-label">
                                    <span className="step-num">Step 1</span>
                                    <span className="step-desc">Customer Info</span>
                                </div>
                            </div>
                            <div className={`stepper-line ${activeStep > 1 ? 'completed' : ''}`} id="stepper_line_1"></div>
                            <div
                                className={`stepper-item ${activeStep === 2 ? 'active' : ''} ${activeStep > 2 ? 'completed' : ''}`}
                                id="stepper_indicator_2"
                                onClick={() => goToStep(2)}
                            >
                                <div className="step-circle" id="circle_2">
                                    {activeStep > 2 ? '✔' : '2'}
                                </div>
                                <div className="step-label">
                                    <span className="step-num">Step 2</span>
                                    <span className="step-desc">Upload Documents</span>
                                </div>
                            </div>
                            <div className={`stepper-line ${activeStep > 2 ? 'completed' : ''}`} id="stepper_line_2"></div>
                            <div
                                className={`stepper-item ${activeStep === 3 ? 'active' : ''}`}
                                id="stepper_indicator_3"
                                onClick={() => goToStep(3)}
                            >
                                <div className="step-circle" id="circle_3">3</div>
                                <div className="step-label">
                                    <span className="step-num">Step 3</span>
                                    <span className="step-desc">Credit Terms</span>
                                </div>
                            </div>
                        </div>

                        {/* RENDER STEP CONTENT */}
                        {activeStep === 1 && (
                            <Step1
                                formData={formData}
                                onChange={handleInputChange}
                                onFileChange={handleFileChange}
                                onRemoveFile={handleRemoveFile}
                                files={files}
                                errors={errors}
                                onNext={goToStep}
                                prefilledFields={prefilledFields}
                            />
                        )}

                        {activeStep === 2 && (
                            <Step2
                                formData={formData}
                                onFileChange={handleFileChange}
                                onRemoveFile={handleRemoveFile}
                                files={files}
                                onBack={goToStep}
                                onNext={goToStep}
                            />
                        )}

                        {activeStep === 3 && (
                            <Step3
                                formData={formData}
                                onChange={handleInputChange}
                                onTradeRefChange={handleTradeRefChange}
                                onBankRefChange={handleBankRefChange}
                                onCreditAmountChange={handleCreditAmountChange}
                                onFileChange={handleFileChange}
                                onRemoveFile={handleRemoveFile}
                                files={files}
                                onBack={goToStep}
                                isSubmit={isSubmit}
                            />
                        )}
                    </form>
                </div>
            </div>
        </div>
    );

    return (
        <React.Fragment>
            <main role="main" className="middle-content-area techtop-middle-div">
                <section className="techtop-customer-form-section">
                    <div className="container">
                        {/* Title Bar */}
                        <div className="techtop-default-title2">
                            <div className="row align-items-center">
                                <div className="col-sm-6">
                                    <h1 className="m-0 dark-color1">New Customer Form</h1>
                                </div>
                                <div className="col-sm-6">
                                    <h2 className="m-0 dark-color1 text-sm-end">{SITE_TITLE}</h2>
                                </div>
                            </div>
                        </div>

                        {/* Success & Error Alert */}
                        {alert.message && (
                            <div className="row mb-3">
                                <div className="col-12">
                                    <AlertMessage
                                        type={alert.type}
                                        message={alert.message}
                                        onClose={() => setAlert({ type: '', message: '' })}
                                        autoCloseTime={8000}
                                    />
                                </div>
                            </div>
                        )}

                        {/* DIV 1: User tries to access url without token in URL */}
                        {!isLoading && pageStatus === 'NO_TOKEN' && renderNoToken()}

                        {/* DIV 2: Invalid or expired link */}
                        {!isLoading && pageStatus === 'INVALID_OR_EXPIRED' && renderInvalidExpired()}

                        {/* DIV 3: Token already in used (when opening URL again) */}
                        {!isLoading && pageStatus === 'ALREADY_USED' && renderAlreadyUsed()}

                        {/* DIV 4: Just submitted application */}
                        {!isLoading && pageStatus === 'SUBMITTED' && renderSubmittedSuccess()}

                        {/* ACTIVE FORM: Rendered when token is verified and ready */}
                        {!isLoading && pageStatus === 'READY' && renderNewCustomerForm()}
                    </div>
                </section>
            </main>
            <Loader isLoading={isLoading || isSubmit} message={isSubmit ? "Submitting Application..." : "Verifying Onboarding Link..."} />
        </React.Fragment>
    );
};

export default CustomerOnBoarding;